/**
 * Battery catalog — popular AU-market storage units.
 *
 * Two coupling modes:
 *   - 'ac':  battery has its own inverter (Powerwall 3, Enphase IQ Battery,
 *            Alpha-ESS SMILE5). It connects to the AC bus directly. The
 *            catalog entry includes inverter spec.
 *   - 'dc':  battery connects to a HYBRID PV inverter's battery port
 *            (Sungrow SBR/SBH, BYD HVS/HVM, GoodWe Lynx). No inverter of
 *            its own — the hybrid PV inverter does the conversion.
 *
 * The picker UX needs to know the difference because DC-coupled
 * batteries require a hybrid inverter to attach to.
 */

import type { Battery } from '../types/topology';

export interface BatteryCatalogEntry {
  manufacturer: string;
  model: string;
  capacity: number;          // kWh (usable)
  coupling: 'ac' | 'dc';
  /** AC-coupled batteries have their own inverter spec. */
  inverter?: { manufacturer: string; model: string; capacity: number; phases: 1 | 3 };
  /** Whether the unit includes a backup gateway (powers loads during outage). */
  backupGateway?: boolean;
  note?: string;
}

export const BATTERY_CATALOG: BatteryCatalogEntry[] = [
  // ----- AC-coupled (own inverter, attaches to AC bus) -----
  {
    manufacturer: 'Tesla',  model: 'Powerwall 3',
    capacity: 13.5, coupling: 'ac', backupGateway: true,
    inverter: { manufacturer: 'Tesla', model: 'Powerwall 3 Inverter', capacity: 11500, phases: 1 },
    note: 'Integrated 11.5 kW inverter + backup'
  },
  {
    manufacturer: 'Tesla',  model: 'Powerwall 2',
    capacity: 13.5, coupling: 'ac', backupGateway: true,
    inverter: { manufacturer: 'Tesla', model: 'Powerwall 2 Inverter', capacity: 5000, phases: 1 },
    note: 'Legacy — 5 kW inverter'
  },
  {
    manufacturer: 'Enphase', model: 'IQ Battery 5P',
    capacity: 5.0, coupling: 'ac', backupGateway: true,
    inverter: { manufacturer: 'Enphase', model: 'Embedded micros', capacity: 3840, phases: 1 },
    note: 'Modular — stack up to 4'
  },
  {
    manufacturer: 'Alpha-ESS', model: 'SMILE5',
    capacity: 13.3, coupling: 'ac', backupGateway: true,
    inverter: { manufacturer: 'Alpha-ESS', model: 'SMILE5 Inverter', capacity: 5000, phases: 1 },
  },
  {
    manufacturer: 'sonnen', model: 'sonnenBatterie 10',
    capacity: 11.0, coupling: 'ac', backupGateway: true,
    inverter: { manufacturer: 'sonnen', model: 'Hybrid Inverter', capacity: 4600, phases: 1 },
    note: 'German-made LFP'
  },

  // ----- DC-coupled (no own inverter, attach to hybrid PV inverter) -----
  {
    manufacturer: 'Sungrow', model: 'SBR096',
    capacity: 9.6, coupling: 'dc',
    note: 'Stackable 3.2 kWh modules — pairs with SH-RT/SH-T'
  },
  {
    manufacturer: 'Sungrow', model: 'SBR128',
    capacity: 12.8, coupling: 'dc',
    note: 'Pairs with SH10RT / SH-T hybrids'
  },
  {
    manufacturer: 'Sungrow', model: 'SBR256',
    capacity: 25.6, coupling: 'dc',
    note: '8 modules — pairs with SH25T'
  },
  {
    manufacturer: 'BYD', model: 'Battery-Box Premium HVS 7.7',
    capacity: 7.7, coupling: 'dc',
    note: 'Pairs with most hybrid inverters'
  },
  {
    manufacturer: 'BYD', model: 'Battery-Box Premium HVS 12.8',
    capacity: 12.8, coupling: 'dc',
    note: '4 modules HVS'
  },
  {
    manufacturer: 'GoodWe', model: 'Lynx Home U 5.4',
    capacity: 5.4, coupling: 'dc',
    note: 'Pairs with GW-ET hybrid inverters'
  },
  {
    manufacturer: 'GoodWe', model: 'Lynx Home U 10.8',
    capacity: 10.8, coupling: 'dc',
    note: '2 modules Lynx Home U'
  },
  {
    manufacturer: 'Pylontech', model: 'US5000',
    capacity: 4.8, coupling: 'dc',
    note: 'Stackable rack-mount'
  },
];

/**
 * Convert a catalog entry into a Battery topology object.
 * For DC-coupled batteries, the caller must specify which hybrid inverter
 * to attach to (connectedTo). For AC-coupled, no inverter id is needed
 * — they sit alongside the rest as their own block.
 */
export function batteryFromCatalog(
  entry: BatteryCatalogEntry,
  opts: { id: string; connectedTo?: string } = { id: 'bat1' }
): Battery {
  const base: Battery = {
    id: opts.id,
    manufacturer: entry.manufacturer,
    model: entry.model,
    capacity: entry.capacity,
    coupling: entry.coupling,
  };
  if (entry.coupling === 'ac' && entry.inverter) base.inverter = entry.inverter;
  if (entry.backupGateway) base.backupGateway = true;
  if (entry.coupling === 'dc' && opts.connectedTo) base.connectedTo = opts.connectedTo;
  return base;
}
